// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

import React, { ReactNode } from "react";

import { CheckRuleCard } from "@/hub/components/check-rule-card";
import { DashboardTemplateCard } from "@/hub/components/dashboard-template-card";
import { ViewCard } from "@/hub/components/view-card";
import { IntegrationsList } from "@/hub/pages/integrations-list-page/components/integrations-list";
import {
  DashboardTemplateMetadata,
  ExtendedIntegrationMetadata,
  Index,
  ViewMetadata,
} from "@/hub/types";
import { CompiledCheckRule } from "@/hub/pages/integration-detail-page/types";

import { localeCompareIgnoreCase } from "@/lib/utils/string";
import { IntegrationDocSection } from "./integration-doc-section";

type IntegrationTabContentProps = {
  index: Index;
  integration: ExtendedIntegrationMetadata;
  dashboardTemplates: DashboardTemplateMetadata[];
  checkRules: CompiledCheckRule[];
  views: ViewMetadata[];
  relatedIntegrations: ExtendedIntegrationMetadata[];
  docs: {
    content: ReactNode;
    id: string;
    label: string;
    mainHeading?: string;
  }[];
};

export function IntegrationTabContent({
  integration,
  dashboardTemplates,
  checkRules,
  views,
  relatedIntegrations,
  docs,
}: IntegrationTabContentProps) {
  return (
    <>
      {docs &&
        docs.length > 0 &&
        docs.map((doc, i) => <IntegrationDocSection doc={doc} key={i} />)}

      {dashboardTemplates.length > 0 && (
        <IntegrationDocSection doc={{ label: "Dashboards", id: "dashboards" }}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {dashboardTemplates
              .slice()
              .sort((a, b) => localeCompareIgnoreCase(a.label, b.label))
              .map((dashboardTemplate) => (
                <DashboardTemplateCard
                  key={dashboardTemplate.id}
                  dashboardTemplate={dashboardTemplate}
                />
              ))}
          </div>
        </IntegrationDocSection>
      )}

      {checkRules.length > 0 && (
        <IntegrationDocSection doc={{ label: "Check Rules", id: "check-rules" }}>
          <div className="flex flex-col gap-6">
            {checkRules
              .slice()
              .sort((a, b) => localeCompareIgnoreCase(a.rule.label, b.rule.label))
              .map((cr) => (
                <CheckRuleCard
                  key={cr.rule.id}
                  rule={cr.rule}
                  body={cr.body}
                  mdxBody={cr.mdxBody}
                  parentIntegrationId={integration.id}
                />
              ))}
          </div>
        </IntegrationDocSection>
      )}

      {views.length > 0 && (
        <IntegrationDocSection doc={{ label: "Views", id: "views" }}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {views
              .slice()
              .sort((a, b) => localeCompareIgnoreCase(a.label, b.label))
              .map((v) => (
                <ViewCard key={v.id} view={v} />
              ))}
          </div>
        </IntegrationDocSection>
      )}

      {relatedIntegrations.length > 0 && (
        <IntegrationDocSection
          doc={{
            label: "Related Integrations",
            id: "related-integrations",
          }}
        >
          <IntegrationsList
            wrapperClassName="xl:grid-cols-2!"
            allIntegrations={relatedIntegrations}
            searchTerm={""}
          />
        </IntegrationDocSection>
      )}
    </>
  );
}
